import React, { useEffect, useState } from "react";
import ErrorTooltip from "Components/ErrorTooltip";
import { checkValidObject, handleServerResponse, saveEditForm, useSafeFormikContext } from "GenericForms/Helper";
import env from "environment_system/env_system";
import { useFormik } from "formik";
import apiGlobal from "global/api.global";
import {
  Row,
  Col,
  Input,
  Label,
  Button,
  CardBody,
  CardFooter,
  CardHeader,
  Card,
  Form
} from "reactstrap";
import { VesselTypeConstant } from "shared/constants";
import FormValuesDebug from "utils/debugTools/FormValuesDebug";
import * as Yup from "yup";
import { RootState } from "index";
import { useSelector, useDispatch } from "react-redux";
import { getCurrentReportAction } from "Store/Generic/ReportingSlice";
import FormLoading from "Components/FormLoading";
import ErrorComponent from "Components/ErrorComponent";
import { commonValidationMessages } from "Components/ValidationErrorMessages";
import { useEditReport } from "VesselReportingInfo/EditCompletedReports/EditReportProvider";

interface OtherDetailsComponentType {
  toggleTab: any;
  activeTab: number;
  setErrorMessage: (value: boolean) => void,
  editReportBool: boolean,
  toggleAddTab: any;
  activeAddTab: number;
}

const OtherDetailsComponent = ({
  toggleTab,
  activeTab,
  setErrorMessage,
  editReportBool,
  toggleAddTab,
  activeAddTab
}: OtherDetailsComponentType) => {
  /** State variables start */
  const [refreshKey, setRefreshKey] = useState(0);
  const { discardwarning } = useEditReport();
  const { VesselID, VoyageID, ReportID, CurrentVessel, CurrentReport, reportingLoading, reportingError } = useSelector((state: RootState) => state.Reporting);
  const otherDetailsCurrentReport = CurrentReport?.data?.deck_officer?.lfonds_other_details ?? null;
  const currentVesselReport = CurrentReport?.data?.vessel_reporting_information ?? null;
  const dispatch = useDispatch();
  let cargoContainer: string = "";
  if (CurrentVessel?.vessel_type_precedence_id === VesselTypeConstant.CONTAINER) {
    cargoContainer = "container";
  } else {
    cargoContainer = "cargo";
  }
  const formikContext = useSafeFormikContext<any>();
  const setEditValues = formikContext?.setValues;
  const editInitialValues = formikContext?.initialValues;
  const editSubmit = formikContext?.submitForm;
  const editIsSubmitting = formikContext?.isSubmitting;
  /** State variables end */

  /** Assign values to initial object of Other Details */
  const getInitialOtherDetailsValues = () => {
    if (checkValidObject(editInitialValues?.deck_officer?.lfonds_other_details)) {
      return editInitialValues?.deck_officer?.lfonds_other_details;
    } else if (checkValidObject(otherDetailsCurrentReport)) {
      return otherDetailsCurrentReport;
    } else {
      return {
        if_cargo_damaged_or_contaminated_during_port_stay: false,
        reason_for_cargo_damage_or_contamination_port: "",
        if_cargo_damaged_or_contaminated_since_last_departure: false,
        reason_for_cargo_damage_or_contamination_departure: "",
        time_remaining_to_next_port: 0,
        closest_distance_from_storm_center: 0,
        time_of_cpa: 0,
        vessel: VesselID,
        voyage_information: VoyageID,
        vessel_reporting_information: ReportID,
      };
    }
  };

  /** Other details' formik object */
  const OtherDetailsFormik: any = useFormik({
    enableReinitialize: true,
    initialValues: getInitialOtherDetailsValues(),
    validationSchema: Yup.object({
      time_remaining_to_next_port: Yup.number().nullable().typeError(`${commonValidationMessages.number}`),
      reason_for_cargo_damage_or_contamination_port: Yup.string().nullable(),
      closest_distance_from_storm_center: Yup.number().nullable().typeError(`${commonValidationMessages.number}`),
      time_of_cpa: Yup.number().typeError(`${commonValidationMessages.number}`),
      reason_for_cargo_damage_or_contamination_departure: Yup.string().max(250, commonValidationMessages.max250Char).nullable(),
    }),
    onSubmit: async (values: any, actions: any) => {
      if (env?.form_validation === false) {
        setErrorMessage(null)
        toggleTab(activeTab + 1);
      }
      actions.setSubmitting(true);
      let responseArray: any = [];
      if (
        values.if_cargo_damaged_or_contaminated_during_port_stay === false &&
        values.if_cargo_damaged_or_contaminated_since_last_departure === false) {
        values.reason_for_cargo_damage_or_contamination_port = ""
      }
      if (checkValidObject(otherDetailsCurrentReport)) {
        responseArray.push(apiGlobal.put(`/lfonds_other_details/${values.id}/`, values));
      } else {
        responseArray.push(apiGlobal.post(`/lfonds_other_details/`, values));
      }
      await handleServerResponse(responseArray).then(async (res: any) => {
        if (res === true) {
          dispatch(getCurrentReportAction(VesselID, ReportID));
          setRefreshKey(refreshKey + 1);
          if (env?.form_validation === true) {
            setErrorMessage(null)
            toggleTab(activeTab + 1);
          }
        } else {
          setErrorMessage(res)
        }
      })
      actions.setSubmitting(false);
    }
  });

  /** UseEffect */
  useEffect(() => {
    OtherDetailsFormik.initialValues = getInitialOtherDetailsValues();
    setRefreshKey(refreshKey + 1);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [otherDetailsCurrentReport]);
  useEffect(() => {
    if (checkValidObject(editInitialValues?.deck_officer?.lfonds_other_details)) {
      setEditValues({
        ...editInitialValues,
        deck_officer: {
          ...editInitialValues?.deck_officer,
          lfonds_other_details: OtherDetailsFormik?.values
        }
      })
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [OtherDetailsFormik?.values])
  /** UseEffect end */
  return (
    <Card className="p-0 mb-0 border-0">
      <CardHeader className="px-0 py-2">
        <div className="text-center">
          <Row>
            <Col>
              <h4 className="page_title pos-start mb-0">Other Details</h4>
              <p className="card-title-desc pos-start">
                All readings since last report
              </p>
            </Col>
          </Row>
        </div>
      </CardHeader>
      {reportingLoading && <FormLoading />}
      {reportingError && <ErrorComponent message="Error loading required data!" />}
      {!reportingLoading && !reportingError && (
        <Form autoComplete="off" onSubmit={OtherDetailsFormik?.handleSubmit} noValidate>
          <CardBody className="mt-2 engineer-card-body">
            <Row className="mb-2">
              {currentVesselReport && currentVesselReport?.reporting_type === "In Port" ? (
                <>
                  <Col lg={3}>
                    <Label>
                      Is {cargoContainer} damaged/contaminated during port
                      stay?
                    </Label>
                  </Col>
                  <Col lg={3}>
                    <div className="square-switch sqswitch mt-3">
                      <Input
                        onFocus={(e) => e.target.select()}
                        type="checkbox"
                        autoFocus={true}
                        switch="none"
                        name="if_cargo_damaged_or_contaminated_during_port_stay"
                        id="if_cargo_damaged_or_contaminated_during_port_stay"
                        onChange={(e: any) =>
                          OtherDetailsFormik?.setFieldValue(
                            "if_cargo_damaged_or_contaminated_during_port_stay",
                            e.target.checked
                          )
                        }
                        defaultChecked={
                          OtherDetailsFormik?.values &&
                          OtherDetailsFormik?.values?.if_cargo_damaged_or_contaminated_during_port_stay
                        }
                      />
                      <Label
                        htmlFor="if_cargo_damaged_or_contaminated_during_port_stay"
                        data-on-label="Yes"
                        data-off-label="No"
                      />
                    </div>
                  </Col>
                  {OtherDetailsFormik?.values &&
                    OtherDetailsFormik?.values?.if_cargo_damaged_or_contaminated_during_port_stay ===
                    true && (
                      <Col lg={3}>
                        <Label className="mb-0">
                          Reason for cargo damage/contamination
                        </Label>
                        <Input
                          onFocus={(e) => e.target.select()}
                          as="textarea"
                          name="reason_for_cargo_damage_or_contamination_port"
                          className="form-control mb-0"
                          rows={4}
                          onBlur={(e: any) => OtherDetailsFormik?.handleChange(e)}
                          defaultValue={OtherDetailsFormik?.values?.reason_for_cargo_damage_or_contamination_port}
                        />
                      </Col>
                    )}
                </>
              ) : (
                <>
                  <Col lg={3} className="px-0">
                    <Label>
                      Is {cargoContainer} damaged/contaminated since last
                      departure?
                    </Label>
                  </Col>
                  <Col lg={3}>
                    <div className="square-switch sqswitch mt-3">
                      <Input
                        onFocus={(e) => e.target.select()}
                        type="checkbox"
                        switch="none"
                        name="if_cargo_damaged_or_contaminated_since_last_departure"
                        id={"if_cargo_damaged_or_contaminated_since_last_departure"}
                        onChange={(e: any) => {
                          OtherDetailsFormik?.setFieldValue(
                            "if_cargo_damaged_or_contaminated_since_last_departure",
                            e.target.checked
                          )
                          if (!e.target.checked) {
                            OtherDetailsFormik?.setFieldValue("reason_for_cargo_damage_or_contamination_departure", "")
                          }
                        }
                        }
                        defaultChecked={
                          OtherDetailsFormik?.values &&
                          OtherDetailsFormik?.values?.if_cargo_damaged_or_contaminated_since_last_departure
                        }
                      />
                      <Label
                        htmlFor={"if_cargo_damaged_or_contaminated_since_last_departure"}
                        data-on-label="Yes"
                        data-off-label="No"
                      />
                    </div>
                  </Col>
                  {OtherDetailsFormik?.values &&
                    OtherDetailsFormik?.values?.if_cargo_damaged_or_contaminated_since_last_departure ===
                    true && (
                      <Col lg={3}>
                        <Label
                          className="mb-0"
                          for="reason_for_cargo_damage_or_contamination_port"
                        >
                          Reason for cargo damage/contamination
                        </Label>
                        <Input
                          onFocus={(e) => e.target.select()}
                          as="textarea"
                          id="reason_for_cargo_damage_or_contamination_departure"
                          name="reason_for_cargo_damage_or_contamination_departure"
                          className="form-control"
                          rows={4}
                          onBlur={(e: any) => OtherDetailsFormik?.handleChange(e)}
                          defaultValue={OtherDetailsFormik?.values?.reason_for_cargo_damage_or_contamination_departure}
                        />
                      </Col>
                    )}
                </>
              )}
            </Row>
            <Row>
              <Col lg={3} className="ps-0">
                <div className="mb-3">
                  <Label
                    className="asteric mb-0"
                    for="time_remaining_to_next_port"
                  >
                    Remaining time to next port
                    <i
                      className="bx bx-info-circle ml-2p"
                      id="time_remaining_to_next_port_msg"
                    ></i>
                  </Label>
                  <div className="input-group">
                    <Input
                      onFocus={(e) => e.target.select()}
                      type="text"
                      className="form-control text-right"
                      id="time_remaining_to_next_port"
                      name="time_remaining_to_next_port"
                      onBlur={OtherDetailsFormik?.handleBlur}
                      onChange={(e: any) => OtherDetailsFormik?.handleChange(e)}
                      defaultValue={OtherDetailsFormik?.values?.time_remaining_to_next_port}
                    />
                    <div className="input-group-text round_border">hr</div>
                    {OtherDetailsFormik?.errors?.time_remaining_to_next_port &&
                      OtherDetailsFormik?.touched?.time_remaining_to_next_port &&
                      env?.form_validation === true && !discardwarning && (
                        <ErrorTooltip
                          target={`time_remaining_to_next_port`}
                          message={OtherDetailsFormik?.errors?.time_remaining_to_next_port}
                          open={
                            OtherDetailsFormik?.errors?.time_remaining_to_next_port
                              ? true
                              : false
                          }
                        />
                      )}
                  </div>
                </div>
              </Col>
              <Col lg={3} className="px-0">
                <div className="mb-3">
                  <Label
                    className="mb-0"
                    for="closest_distance_from_storm_center"
                  >
                    Distance to closest storm center
                    <i
                      className="bx bx-info-circle ml-2p"
                      id="closest_distance_from_storm_center_msg"
                    />
                  </Label>
                  <div className="input-group">
                    <Input
                      onFocus={(e) => e.target.select()}
                      type="text"
                      className="form-control text-right"
                      id="closest_distance_from_storm_center"
                      name="closest_distance_from_storm_center"
                      onBlur={(e: any) => OtherDetailsFormik?.handleChange(e)}
                      defaultValue={OtherDetailsFormik?.values?.closest_distance_from_storm_center}
                    />
                    <div className="input-group-text round_border">nm</div>
                    {OtherDetailsFormik?.errors?.closest_distance_from_storm_center &&
                      env?.form_validation === true && !discardwarning && (
                        <ErrorTooltip
                          target={`closest_distance_from_storm_center`}
                          message={
                            OtherDetailsFormik?.errors?.closest_distance_from_storm_center
                          }
                          open={
                            OtherDetailsFormik?.errors?.closest_distance_from_storm_center
                              ? true
                              : false
                          }
                        />
                      )}
                  </div>
                </div>
              </Col>
              <Col lg={3}>
                <div className="mb-3">
                  <Label className="mb-0" for="time_of_cpa">
                    Time of CPA
                    <i
                      className="bx bx-info-circle ml-2p"
                      id="time_of_cpa_msg"
                    ></i>
                  </Label>
                  <div className="input-group">
                    <Input
                      onFocus={(e) => e.target.select()}
                      type="text"
                      className="form-control text-right"
                      id="time_of_cpa"
                      name="time_of_cpa"
                      onBlur={(e: any) => OtherDetailsFormik?.handleChange(e)}
                      defaultValue={OtherDetailsFormik?.values?.time_of_cpa}
                    />
                    <div className="input-group-text round_border">hr</div>
                    {OtherDetailsFormik?.errors?.time_of_cpa &&
                      env?.form_validation === true && !discardwarning && (
                        <ErrorTooltip
                          target={`time_of_cpa`}
                          message={OtherDetailsFormik?.errors?.time_of_cpa}
                          open={OtherDetailsFormik?.errors?.time_of_cpa ? true : false}
                        />
                      )}
                  </div>
                </div>
              </Col>
            </Row>
          </CardBody>
          <CardFooter className="p-2 py-3">
            <Row className="d-flex align-items-center flex-row-reverse">
              <Col lg={10}>
                <div className="d-flex flex-wrap gap-3 justify-content-end ">
                  {/* {editReportBool && (
                    <Button
                      type="reset"
                      color="danger"
                      className="btn_size_cstm"
                      onClick={() => {
                        setEditValues({
                          ...editValues,
                          deck_officer: {
                            ...editValues?.deck_officer,
                            lfonds_other_details: editInitialValues?.deck_officer?.lfonds_other_details,
                          }
                        });
                      }}
                    >
                      Reset
                    </Button>
                  )} */}
                  <Button
                    type={editReportBool ? "button" : "submit"}
                    color="primary"
                    className="btn_size_cstm"
                    disabled={OtherDetailsFormik?.isSubmitting || editIsSubmitting}
                    onClick={async () => {
                      const validationErrors = await OtherDetailsFormik?.validateForm();
                      Object.keys(OtherDetailsFormik?.values || {}).forEach((key: any) => {
                        OtherDetailsFormik?.setFieldTouched(key, true);
                      });
                      if (editReportBool && Object.keys(validationErrors).length === 0) {
                        await saveEditForm(editSubmit, toggleTab, activeTab);
                      }
                    }}
                  >
                    {(OtherDetailsFormik?.isSubmitting || editIsSubmitting) && (
                      <i className="ms-2 spinner-border spinner-border-sm text-light me-2" />
                    )}
                    Next <i className="bx bx-chevron-right ms-1" />
                  </Button>
                </div>
              </Col>
              <Col lg={2}>
                <Button
                  type="button"
                  color="primary"
                  className="btn_size_cstm"
                  onClick={() => {
                    OtherDetailsFormik?.setErrors({});
                    setErrorMessage(null);
                    if (activeAddTab === 1) {
                      toggleTab(activeTab - 1);
                    } else {
                      toggleAddTab(activeAddTab - 1);
                    }
                  }}
                >
                  <i className="bx bx-chevron-left me-1" /> Previous
                </Button>
              </Col>
            </Row>
          </CardFooter>
          <FormValuesDebug
            values={[
              OtherDetailsFormik?.values,
              OtherDetailsFormik?.errors,
              OtherDetailsFormik.initialValues,
              OtherDetailsFormik?.touched,
            ]}
          />
        </Form>
      )}
    </Card>
  );
};

export default OtherDetailsComponent;
